import { useEffect, useState } from "react";
import { Box, Flex, Text, Heading, Badge } from "@chakra-ui/react";
import {
  AppButton,
  AppTable,
  TableRow,
  TableCell,
  AppModal,
  AppInput,
  AppSelect,
} from "../../components";
import { colors } from "../../theme";
import { PlusIcon, EditIcon, DeleteIcon, ViewIcon, PromoIcon } from "../../utils/svg";
import { Controller, useForm } from "react-hook-form";
import {
  usePromoCodes,
  useCreatePromoCode,
  useUpdatePromoCode,
  useDeletePromoCode,
  useMalls,
  useRestaurants,
} from "../../services/useQueries";
import { useDispatch } from "react-redux";
import { setLoading } from "../../redux/reducer/utilsSlice";
import { useAdminRole } from "../../utils/useAdminRole";

const PromoCodeManagementScreen = () => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [page, setPage] = useState(1);
  const dispatch = useDispatch();
  // Fetch promo codes and malls
  const { data: promoCodes, isLoading: promoCodesLoading } = usePromoCodes();
  const promoCodesData = promoCodes?.data || [];

  const { data: malls = [], isLoading: mallsLoading } = useMalls();
  const { isMallAdmin, managedMallId } = useAdminRole();
  const selectedMallId = watch("mallId");
  const { data: restaurants, isLoading: restaurantsLoading } = useRestaurants(selectedMallId);
  const restaurantsList = restaurants?.data || [];
  const createPromoCodeMutation = useCreatePromoCode();
  const updatePromoCodeMutation = useUpdatePromoCode();
  const deletePromoCodeMutation = useDeletePromoCode();
  const discountTypeOptions = [
    { value: "percentage", label: "Percentage (%)" },
    { value: "fixed", label: "Fixed Amount" },
  ];

  const headers = [
    "Promo Code",
    "Discount %",
    "Mall",
    "Shop",
    "Time Duration",
    "Actions",
  ];

  const handleAddPromo = () => {
    setSelectedPromo(null);
    // A mall admin's promos always belong to their mall (GAP-018); the
    // backend forces this regardless of what is submitted.
    reset(isMallAdmin ? { mallId: managedMallId } : {});
    setIsModalOpen(true);
  };

  const handleEditPromo = (promo) => {
    setSelectedPromo(promo);
    reset(promo);
    setIsModalOpen(true);
  };

  const handleDeletePromo = (promo) => {
    if (window.confirm("Are you sure you want to delete this promo code?")) {
      deletePromoCodeMutation.mutate(promo.id);
    }
  };

  const handleViewPromo = (promo) => {
    setSelectedPromo(promo);
    setIsViewModalOpen(true);
  };

  const onSubmit = (data) => {
    const payload = {
      mallId: data.mallId,
      restaurantId: data.restaurantId,
      code: data.code,
      discountPercentage: Number(data.discountPercentage),
      // startDate is auto-generated server-side; only endDate is sent.
      endDate: data.endDate,
    };
    if (selectedPromo && selectedPromo.id) {
      updatePromoCodeMutation.mutate(
        { id: selectedPromo.id, data: payload },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            reset();
          },
        }
      );
    } else {
      createPromoCodeMutation.mutate(payload, {
        onSuccess: () => {
          setIsModalOpen(false);
          reset();
        },
      });
    }
  };
useEffect(() => {
  dispatch(setLoading(promoCodesLoading || mallsLoading || restaurantsLoading));
}, [promoCodesLoading, mallsLoading, restaurantsLoading]);
  const renderRow = (promo, index) => (
    <TableRow key={promo.id || index}>
      <TableCell fontWeight="600" fontFamily="mono">
        {promo.code}
      </TableCell>
      <TableCell>{promo.discountPercentage}%</TableCell>
      <TableCell>
        {malls.find((m) => m.id === promo.mallId)?.name || "-"}
      </TableCell>
      <TableCell>{promo.Restaurant?.name || "-"}</TableCell>
      <TableCell>
        {promo.startDate?.slice(0, 10)} / {promo.endDate?.slice(0, 10)}
      </TableCell>

      <TableCell>
        <Flex gap={2}>
          <AppButton
            variant="ghost"
            size="sm"
            onClick={() => handleViewPromo(promo)}
            leftIcon={<ViewIcon boxSize={4} />}
          />
          <AppButton
            variant="ghost"
            size="sm"
            onClick={() => handleEditPromo(promo)}
            leftIcon={<EditIcon boxSize={4} />}
          />
          <AppButton
            variant="ghost"
            size="sm"
            onClick={() => handleDeletePromo(promo)}
            leftIcon={<DeleteIcon boxSize={4} color="#EF4444" />}
          />
        </Flex>
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color={colors.text} mb={1}>
            Promo Code Management
          </Heading>
          <Text color={colors.textSecondary}>
            Manage promotional codes and discounts
          </Text>
        </Box>
        <AppButton
          variant="primary"
          leftIcon={<PromoIcon boxSize={5} />}
          onClick={handleAddPromo}
        >
          Add Promo Code
        </AppButton>
      </Flex>

      {/* Table */}
      <AppTable
        headers={headers}
        data={promoCodesData}
        renderRow={renderRow}
        loading={promoCodesLoading}
        pagination={{
          page,
          totalPages: 1,
          onPageChange: setPage,
        }}
      />

      {/* Add/Edit Modal */}
      <AppModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedPromo ? "Edit Promo Code" : "Add New Promo Code"}
        size="lg"
        footer={
          <Flex gap={3} justify="flex-end" w="100%">
            <AppButton
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              isLoading={
                createPromoCodeMutation.isPending ||
                updatePromoCodeMutation.isPending
              }
            >
              {selectedPromo ? "Update Promo" : "Add Promo"}
            </AppButton>
          </Flex>
        }
      >
        <Flex direction="column" gap={4}>
          <Controller
            control={control}
            name="mallId"
            rules={{ required: "Mall is required" }}
            render={({ field }) => (
              <AppSelect
                label="Mall"
                placeholder="Select mall"
                options={
                  // A mall admin's locked select only lists their own mall.
                  (isMallAdmin
                    ? malls.filter((m) => m.id === managedMallId)
                    : malls
                  ).map((m) => ({ value: m.id, label: m.name }))
                }
                value={field.value}
                onChange={(val) => {
                  field.onChange(val);
                  setValue("restaurantId", undefined);
                }}
                // Mall admins can only issue promos for their own mall.
                disabled={isMallAdmin}
                error={errors.mallId}
              />
            )}
          />
          <Controller
            control={control}
            name="restaurantId"
            rules={{ required: "Pick the shop the code is for" }}
            render={({ field }) => (
              <AppSelect
                label="Shop"
                placeholder={
                  selectedMallId ? "Select shop" : "Select mall first"
                }
                options={restaurantsList.map((r) => ({
                  value: r.restaurantId,
                  label: r.name,
                }))}
                value={field.value}
                onChange={(val) => {
                  field.onChange(val); // val is already the restaurantId string
                }}
                error={errors.restaurantId}
                disabled={!selectedMallId}
              />
            )}
          />
          <AppInput
            label="Promo Code"
            name="code"
            placeholder="Enter promo code (e.g., SUMMER2025)"
            register={register}
            errors={errors}
            required
            inputProps={{ textTransform: "uppercase" }}
          />
          <AppInput
            label="Discount Percentage"
            name="discountPercentage"
            type="number"
            placeholder="Enter discount percentage"
            register={register}
            errors={errors}
            required
          />
          {/* Start date is auto-set to "now" by the backend (a promo code
              goes live the moment it's created), so we only collect the end
              date. Showing a Start Date picker here was misleading — the
              value was silently ignored on save. */}
          <AppInput
            label="End Date"
            name="endDate"
            type="date"
            register={register}
            errors={errors}
            required
          />
        </Flex>
      </AppModal>

      {/* View Modal */}
      <AppModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Promo Code Details"
        size="md"
        footer={
          <Flex gap={3} justify="flex-end" w="100%">
            <AppButton
              variant="secondary"
              onClick={() => setIsViewModalOpen(false)}
            >
              Close
            </AppButton>
          </Flex>
        }
      >
        {selectedPromo && (
          <Flex direction="column" gap={3}>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Code
              </Text>
              <Text color={colors.textSecondary}>{selectedPromo.code}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Discount
              </Text>
              <Text color={colors.textSecondary}>
                {selectedPromo.discountPercentage}%
              </Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Mall
              </Text>
              <Text color={colors.textSecondary}>
                {malls.find((m) => m.id === selectedPromo.mallId)?.name || "-"}
              </Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Restaurant
              </Text>
              <Text color={colors.textSecondary}>
                {restaurantsList.find(
                  (r) => r.restaurantId === selectedPromo.restaurantId
                )?.name || "-"}
              </Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Start Date
              </Text>
              <Text color={colors.textSecondary}>
                {selectedPromo.startDate?.slice(0, 10)}
              </Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                End Date
              </Text>
              <Text color={colors.textSecondary}>
                {selectedPromo.endDate?.slice(0, 10)}
              </Text>
            </Box>
          </Flex>
        )}
      </AppModal>
    </Box>
  );
};

export default PromoCodeManagementScreen;
